/**
 * <UrgentPoBanner country />  - Retail POs whose cancellation deadline is within 14 days (GET /api/ai/urgent-pos).
 * Shown at the top of the Retail POs page; renders nothing when there is none. Moved here from the old AI page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, MapPin, Package } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';
import { LoadError } from './LoadError';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const TONE = {
  CRITICAL: 'border-red-200 bg-red-50 text-red-800', HIGH: 'border-orange-200 bg-orange-50 text-orange-800',
  MEDIUM: 'border-amber-200 bg-amber-50 text-amber-800',
};

export const UrgentPoBanner = ({ country }) => {
  const [state, setState] = useState({ rows: [], error: null, loading: true });
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${API}/ai/urgent-pos`, { params: { country }, headers: getAuthHeaders(), signal: controller.signal })
      .then(res => setState({ rows: res.data?.urgent_pos || [], error: null, loading: false }))
      .catch(error => { if (!controller.signal.aborted) setState({ rows: [], error, loading: false }); });
    return () => controller.abort();
  }, [country, version]);
  if (state.error) return <LoadError what="urgent PO deadlines" error={state.error} onRetry={() => setVersion(v => v + 1)} retrying={state.loading} testId="urgent-po-load-error" />;
  if (!state.rows.length) return null;
  return (
    <section className="rounded-xl border border-red-200 bg-red-50/60 p-3" aria-labelledby="urgent-po-title" data-testid="urgent-po-banner">
      <h2 id="urgent-po-title" className="flex items-center gap-2 text-sm font-semibold text-red-900">
        <AlertTriangle className="h-4 w-4" aria-hidden="true" /> {state.rows.length} PO{state.rows.length === 1 ? '' : 's'} close to the cancellation deadline
      </h2>
      <ul className="mt-2 flex flex-wrap gap-2">
        {state.rows.slice(0, 8).map(po => (
          <li key={`${po.po_number}-${po.ship_to_fc || ''}`} className={`rounded-lg border px-2.5 py-1.5 text-xs ${TONE[po.urgency] || 'border-blue-200 bg-blue-50 text-blue-800'}`}>
            <span className="font-mono font-semibold">{po.po_number}</span>
            <span className="ml-2 inline-flex items-center gap-1"><MapPin className="h-3 w-3" aria-hidden="true" />{po.fc_name}</span>
            <span className="ml-2 inline-flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden="true" />{po.days_left} day{po.days_left === 1 ? '' : 's'} left ({po.deadline})</span>
            <span className="ml-2 inline-flex items-center gap-1"><Package className="h-3 w-3" aria-hidden="true" />{po.remaining_units} units</span>
          </li>
        ))}
        {state.rows.length > 8 && <li className="px-2 py-1.5 text-xs text-red-800">+{state.rows.length - 8} more</li>}
      </ul>
    </section>
  );
};

export default UrgentPoBanner;
